// TabContent1.js
import React, { useState, useEffect, useMemo } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, InputBase } from '@mui/material';
import { tokens } from '../../../../../theme';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { yupRequired, yupNumberRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import { countriesFromLocale } from '../../../../../utils/countries';
import { countryCodeNL } from '../../../../../utils/constants';
import { countryRequiresHouseNumber, addressHasHouseNumber } from '../../../../../utils/address';
import { useGetShippersSelectQuery } from '../../../../../newapi/shipper/shipperSlice';
import { useGetServicePointsQuery } from '../../../../../newapi/order/ordersSlice';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const [loaded, setLoaded] = useState(false);
    const dispatch = useDispatch();

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    // Fetch shippers with service points support
    const { data: shippersData } = useGetShippersSelectQuery({ withServicePoints: true });

    // State for shipper and service point selection
    const [selectedShipperIndex, setSelectedShipperIndex] = useState(-1);
    const [selectedServicePointIndex, setSelectedServicePointIndex] = useState(-1);

    // Prepare shipper options with "home_delivery" as first option
    const shipperOptions = useMemo(() => {
        if (!shippersData) return [];
        return [
            { id: 'home_delivery', name: t('home_delivery'), type: 'home_delivery' },
            ...shippersData.map(x => {
                return {
                    id: x.id,
                    name: `${t("service_point")}: ${x.name}`,
                    type: x.type
                }
            })
        ];
    }, [shippersData, t]);

    // Restore selected shipper index from persisted data
    useEffect(() => {
        if (data.shipperId && shipperOptions.length > 0) {
            const index = shipperOptions.findIndex(s => s.id === data.shipperId);
            if (index !== -1 && index !== selectedShipperIndex) {
                setSelectedShipperIndex(index);
            }
        }
    }, [data.shipperId, shipperOptions]);

    // Get selected shipper
    const selectedShipper = useMemo(() => {
        if (selectedShipperIndex === -1) return null;
        return shipperOptions[selectedShipperIndex];
    }, [selectedShipperIndex, shipperOptions]);

    // Check if service point dropdown should be shown
    const showServicePointDropdown = useMemo(() => {
        if (!selectedShipper || selectedShipper.id === 'home_delivery') return false;
        if (data.shippingAddress?.country_code !== countryCodeNL) return false;
        // Check if shipper is Dutch postal service (has type property)
        if (!selectedShipper.type) return false;
        // Also show if we have persisted service points
        return true;
    }, [selectedShipper, data.shippingAddress?.country_code]);

    // Fetch service points when conditions are met
    const shouldFetchServicePoints = showServicePointDropdown && !isNaN(parseInt(data.shipperId)) && data.shippingAddress?.postal && 
    data.shippingAddress?.city && selectedShipper?.type;
    const { data: servicePointsData } = useGetServicePointsQuery(
        {
            postal: data.shippingAddress?.postal,
            city: data.shippingAddress?.city,
            type: selectedShipper?.type
        },
        { skip: !shouldFetchServicePoints }
    );

    // Transform service point data based on shipper type
    const transformedServicePoints = useMemo(() => {
        // First check if we have persisted service points in data
        if (data.servicePointsArray && data.servicePointsArray.length > 0) {
            return data.servicePointsArray;
        }

        // Otherwise transform from fresh API data
        if (!servicePointsData) return [];

        switch (selectedShipper?.type) {
            case 'dhl':
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.street || ''} ${sp.houseNumber || ''} - ${sp.postalCode || ''} ${sp.city || ''} - ${sp.distance}km`,
                    rawData: sp
                }));
            case 'ampere':
            case 'zineps':
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.street || ''} ${sp.houseNumber || ''} - ${sp.postalCode || ''} ${sp.city || ''} - ${sp.distance}km`,
                    rawData: sp
                }));
            case 'postnl':
                // PostNL returns a pre-combined address string and no distance value.
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    displayName: `${sp.name} - ${sp.address || ''} - ${sp.postalCode || ''} ${sp.city || ''}`,
                    rawData: sp
                }));
            case 'innosendv2':
                // Innosend brokers several couriers, so the points are grouped under a heading per
                // courier - carrierGroup is the short key ('dhl', 'postnl') rather than the full courier
                // name, which reads as "DHLEuroPlus Innosend".
                // Its address is pre-combined like PostNL's and it returns no distance value.
                return servicePointsData.servicePoints.map((sp, index) => ({
                    id: sp.id,
                    carrierGroup: sp.carrierGroup ? sp.carrierGroup.toUpperCase() : sp.carrierGroup,
                    displayName: `${sp.name} - ${sp.address || ''} - ${sp.postalCode || ''} ${sp.city || ''}`,
                    rawData: sp
                }));
            default:
                return [];
        }
    }, [servicePointsData, selectedShipper?.type, data.servicePointsArray]);

    // Save transformed service points to progression data when they change
    useEffect(() => {
        if (servicePointsData && transformedServicePoints.length > 0 && !data.servicePointsArray) {
            props.handleDataUpdate({ servicePointsArray: transformedServicePoints });
        }
    }, [transformedServicePoints, servicePointsData]);

    // Restore selected service point index from persisted data
    useEffect(() => {
        if (data.shippingAddress?.pickupPoint && transformedServicePoints.length > 0) {
            const index = transformedServicePoints.findIndex(sp =>
                sp.rawData.id === data.shippingAddress.pickupPoint.id
            );
            if (index !== -1 && index !== selectedServicePointIndex) {
                setSelectedServicePointIndex(index);
            }
        }
    }, [data.shippingAddress?.pickupPoint, transformedServicePoints]);

    useEffect(() => {
        if(!data.shippingAddress?.country_code) {
            handleInput('shippingAddress.country_code', countryCodeNL);
        }
    }, []);

    const handleInput = (key, val) => {
        if (key.indexOf('.') > -1) {
            key = key.split('.')[1];

            const shippingAddress = data.shippingAddress ? { ...data.shippingAddress } : {};
            shippingAddress[key] = val;
            props.handleDataUpdate({ shippingAddress: shippingAddress });
            dispatch(setTabErrors({ ...props.errors, [key]: '' }));
        } else {
            props.handleDataUpdate({ [key]: val });
            dispatch(setTabErrors({ ...props.errors, [key]: '' }));
        }
    };

    // Handle shipper selection
    const handleShipperChange = (key, val) => {
        // val is the ID from changeField="id"
        const shipper = shipperOptions.find(s => s.id === val);
        if (shipper) {
            const shipperIndex = shipperOptions.findIndex(s => s.id === val);
            setSelectedShipperIndex(shipperIndex);

            props.handleDataUpdate({
                shipperId: shipper.id,
                shipperType: shipper.type
            });

            // Reset service point selection and array when shipper changes
            setSelectedServicePointIndex(-1);
            props.handleDataUpdate({
                servicePointsArray: null
            });

            // Set shipper and clear pickup point
            const shippingAddress = data.shippingAddress ? { ...data.shippingAddress } : {};
            shippingAddress["pickupCarrier"] = shipper.type;
            shippingAddress["pickupPoint"] = null;
            props.handleDataUpdate({ shippingAddress: shippingAddress });
        }
    };

    // Handle service point selection
    const handleServicePointChange = (key, val) => {
        if (val) {
            const arrIndex = transformedServicePoints.findIndex(x => x.id === val);
            if (arrIndex < 0) return;
            
            setSelectedServicePointIndex(arrIndex);
            const servicePoint = transformedServicePoints[arrIndex];

            const shippingAddress = data.shippingAddress ? { ...data.shippingAddress } : {};
            shippingAddress["pickupPoint"] = servicePoint?.rawData;
            props.handleDataUpdate({ shippingAddress: shippingAddress });
        }
    };

    // Set virtual product is true for POST request
    useEffect(() => {
        if(loaded) {
            props.handleDataUpdate({ isVirtual: true });
        }
        setLoaded(true);
    }, [loaded]);

    return (
        <>
            <Box 
                sx={{
                    display:'flex',
                    flexDirection:"column",
                }}
            >
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("first_name")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.firstName"
                            value={data.shippingAddress ? data.shippingAddress.firstName : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.firstName`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("last_name")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.lastName"
                            value={data.shippingAddress ? data.shippingAddress.lastName : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.lastName`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("company")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.company"
                            value={data.shippingAddress ? data.shippingAddress.company : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.company`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("phone")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.phone"
                            value={data.shippingAddress ? data.shippingAddress.phone : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.phone`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("email")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="email"
                            value={data.email ?? ''}
                            onChange={handleInput}
                            error={props.errors.email}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("address1")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.address1"
                            value={data.shippingAddress ? data.shippingAddress.address1 : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.address1`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("address2")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.address2"
                            value={data.shippingAddress ? data.shippingAddress.address2 : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.address2`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("postal")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.postal"
                            value={data.shippingAddress ? data.shippingAddress.postal : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.postal`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("city")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.city"
                            value={data.shippingAddress ? data.shippingAddress.city : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.city`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("country")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                    <ShValidatedInputDropdown
                            name="shippingAddress.country_code"
                            displayName="name"
                            changeField="alpha2"
                            options={countries ?? null}
                            noSelection={t("choose_option")}
                            onChange={handleInput}
                            selected={countries?.findIndex(country => (data.shippingAddress?.country_code && data.shippingAddress.country_code === country.alpha2)) ?? -1}
                            error={props.errors[`shippingAddress.country_code`]}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_relation_placeholder")
                            }}
                            styles={{
                                minWidth:300
                            }}

                            disallowDeselection
                        />
                    </Box>
                </Box>
                {data.shippingAddress?.country_code === countryCodeNL && shipperOptions.length > 1 && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                            }}
                        >
                            <Text>{t("delivery_option")} ({t("optional")})</Text>
                        </Box>

                        <Box
                            sx={{
                                flex:1,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                name="shipper"
                                displayName="name"
                                changeField="id"
                                options={shipperOptions}
                                noSelection={t("choose_option")}
                                onChange={handleShipperChange}
                                selected={selectedShipperIndex}
                                error={props.errors.shipper}
                                closeOnSelection
                                styles={{
                                    minWidth:300
                                }}
                            />
                        </Box>
                    </Box>
                )}
                {showServicePointDropdown && transformedServicePoints.length > 0 && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                            }}
                        >
                            <Text>{t("service_point")}</Text>
                        </Box>

                        <Box
                            sx={{
                                flex:1,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                name="servicePoint"
                                displayName="displayName"
                                optionGroupKey="carrierGroup"
                                changeField="id"
                                options={transformedServicePoints}
                                noSelection={t("choose_option")}
                                onChange={handleServicePointChange}
                                selected={selectedServicePointIndex}
                                error={props.errors.servicePoint}
                                closeOnSelection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search")
                                }}
                                styles={{
                                    minWidth:300
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};

const AddressDetails = (props) => {
    const {t} = useTranslation();

    const addressFields = [
        'address1',
        'postal',
        'city',
        'country_code',
        'firstName',
        'lastName'
    ];
    
    // The street line must contain a house number in NL, BE and DE.
    const validateHouseNumber = (value, data) =>
        countryRequiresHouseNumber(data.shippingAddress?.country_code) && !addressHasHouseNumber(value)
            ? t("address_house_number_required")
            : null;

    const conditionalSchema = [
        ...addressFields.map(field => ({
            key: `shippingAddress.${field}`,  // Use shippingAddress prefix
            errorField: t(field),
            alwaysRequired: false,
            condition: (data) => true,
            type: "text",
            ...(field === 'address1' ? { validate: validateHouseNumber } : {})
        }))
    ];

    return (
        <ProgressionTab 
            tabId="tabAddressDetails" 
            subtitle={t("address_details")} 
            step={2}
            nextTab={"tabOrderlines"}
            maxSteps={3}
            conditionalSchema={conditionalSchema}
        >
            <Content/>
        </ProgressionTab>
    )
}

export default AddressDetails;